// Weather + Travel widget for the dashboard. Renders for the user's
// next upcoming match — shows:
//   - Big temperature + condition icon for kickoff time
//   - Feels-like, humidity, wind, rain chance
//   - Next 3-4 days mini-row (high/low + icon)
//   - "Plan to leave by" — derived from match.time minus a 25-min buffer
//   - "Open in Google Maps" deep link for the venue
//
// Hides itself gracefully when:
//   1. No upcoming match
//   2. Match has no venue
//   3. Server has no WEATHER_API_KEY (returns 503)
//   4. Geocoding fails (returns 404)
//
// All API key handling lives server-side; this component just hits
// /api/v1/weather/forecast?venue=...&date=...&time=... .

import React, { useEffect, useMemo, useState } from "react";
import {
  Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning,
  Sun, CloudSun, Moon, CloudMoon, Wind, Droplets,
  Navigation, ExternalLink
} from "lucide-react";
import { api } from "../api";
import { fmtTimeEastern, centralToMs } from "../utils/time";

// Map OpenWeatherMap icon codes (e.g. "10d", "01n") to Lucide icons.
function WxIcon({ code, size = 32, className }) {
  const base = (code || "").slice(0, 2);   // "01" – "13"
  const dayNight = (code || "").slice(2);  // "d" or "n"
  const isNight = dayNight === "n";
  const props = { size, className, strokeWidth: 1.75 };
  if (base === "01") return isNight ? <Moon {...props}/>     : <Sun {...props}/>;
  if (base === "02") return isNight ? <CloudMoon {...props}/> : <CloudSun {...props}/>;
  if (base === "03" || base === "04") return <Cloud {...props}/>;
  if (base === "09" || base === "10") return <CloudRain {...props}/>;
  if (base === "11") return <CloudLightning {...props}/>;
  if (base === "13") return <CloudSnow {...props}/>;
  if (base === "50") return <CloudFog {...props}/>;
  return <Cloud {...props}/>;
}

function fmtDay(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "short" });
}

// Plan-to-leave time = match time (interpreted as Eastern) − buffer.
const LEAVE_BUFFER_MIN = 25;
function planLeaveBy(matchDate, matchTime) {
  if (!matchDate || !matchTime) return null;
  const atMs = centralToMs(matchDate, matchTime);
  if (!Number.isFinite(atMs)) return null;
  return fmtTimeEastern(atMs - LEAVE_BUFFER_MIN * 60 * 1000);
}

export default function WeatherTravelCard({ nextMatch }) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });

  // Decide whether we even need to call the API.
  const ready = !!(nextMatch?.venue && nextMatch?.date);

  useEffect(() => {
    if (!ready) {
      setState({ status: "idle", data: null, error: null });
      return;
    }
    let cancelled = false;
    setState(s => ({ ...s, status: "loading" }));
    api.weatherForecast({
      venue: nextMatch.venue,
      date:  nextMatch.date,
      time:  nextMatch.time || "12:00"
    })
      .then(data => { if (!cancelled) setState({ status: "ok",   data, error: null }); })
      .catch(err  => { if (!cancelled) setState({ status: "err",  data: null, error: err }); });
    return () => { cancelled = true; };
  }, [ready, nextMatch?.venue, nextMatch?.date, nextMatch?.time]);

  const leaveBy = useMemo(
    () => planLeaveBy(nextMatch?.date, nextMatch?.time),
    [nextMatch?.date, nextMatch?.time]
  );
  const mapsUrl = nextMatch?.venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(nextMatch.venue)}`
    : null;

  // Hide widget entirely when there's nothing to show or the API is
  // not configured (503 from server). Errors with 4xx/500 also hide
  // so we don't show broken UI to end users.
  if (!ready) return null;
  if (state.status === "err") return null;

  // Loading skeleton — keeps layout stable.
  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="wxCard wxCard--loading" aria-label="Weather loading">
        <div className="wxCard-skeletonHero"/>
        <div className="wxCard-skeletonRow"/>
      </div>
    );
  }

  const d = state.data;
  const at = d.atMatch;

  return (
    <div className="wxCard">
      <div className="wxCard-head">
        <div className="wxCard-headLeft">
          <span className="wxCard-eyebrow">Match day · {fmtDay(nextMatch.date)}</span>
          <h3 className="wxCard-loc">{d.location.name}{d.location.state ? `, ${d.location.state}` : ""}</h3>
        </div>
        {leaveBy && (
          <div className="wxCard-leave">
            <Navigation size={11}/>
            <span><b>Leave by {leaveBy} ET</b><small>kickoff − {LEAVE_BUFFER_MIN} min</small></span>
          </div>
        )}
      </div>

      {at && (
        <div className="wxCard-hero">
          <div className="wxCard-heroLeft">
            <WxIcon code={at.icon} size={56} className="wxCard-heroIcon"/>
            <div>
              <b className="wxCard-temp">{at.temp}°</b>
              <small className="wxCard-desc">{at.descLong || at.desc}</small>
            </div>
          </div>
          <ul className="wxCard-vitals">
            <li><span>Feels like</span><b>{at.feelsLike}°</b></li>
            <li><Droplets size={11}/><span>Rain</span><b>{at.rainChance}%</b></li>
            <li><Wind size={11}/><span>Wind</span><b>{at.windMph} mph</b></li>
            <li><span>Humidity</span><b>{at.humidity ?? "—"}%</b></li>
          </ul>
        </div>
      )}

      {(d.daily || []).length > 1 && (
        <div className="wxCard-daily" aria-label="Forecast">
          {d.daily.map(day => (
            <div key={day.day} className="wxCard-dailyCell">
              <small>{fmtDay(day.day)}</small>
              <WxIcon code={day.icon} size={20} className="wxCard-dailyIcon"/>
              <span><b>{day.hi}°</b> <em>{day.lo}°</em></span>
            </div>
          ))}
        </div>
      )}

      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="wxCard-mapsLink"
        >
          <Navigation size={12}/> Get directions <ExternalLink size={11}/>
        </a>
      )}
    </div>
  );
}
